import { createClientError } from '../Errors';
import { GetAttachmentSignatureResponse, GetNotificationsRequest, GetNotificationsResponse, NotificationItem } from '../Models';
import { getToken, storeToken } from '../TokenStore';

export class InboxClient {
    disposed = false;

    constructor(private email: string, private baseUrl: string, private onTokenRequest: () => Promise<string>) {}

    dispose = (): void => {
        this.disposed = true;
    };

    async getList(request: GetNotificationsRequest): Promise<GetNotificationsResponse> {
        return this.wrapPromise(
            (async () => {
                let params = Object.entries(request)
                    .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
                    .join('&');
                if (params) {
                    params = '?' + params;
                }
                const response = await this.fetchInternal(`${this.baseUrl}/v1/notifications${params}`, {
                    method: 'GET',
                });
                if (response.ok) {
                    return await response.json();
                } else {
                    throw await createClientError(response);
                }
            })(),
        );
    }

    async getNotification(notificationId: string): Promise<NotificationItem> {
        return this.wrapPromise(
            (async () => {
                const response = await this.fetchInternal(`${this.baseUrl}/v1/notifications/${notificationId}`, {
                    method: 'GET',
                });
                if (response.ok) {
                    return response.json();
                } else {
                    throw await createClientError(response);
                }
            })(),
        );
    }

    async markAsRead(notificationIds: string[]): Promise<void> {
        return this.wrapPromise(
            (async () => {
                const response = await this.fetchInternal(`${this.baseUrl}/v1/notifications/read`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        ids: notificationIds,
                    }),
                });
                if (!response.ok) {
                    throw await createClientError(response);
                }
            })(),
        );
    }

    async markAllAsRead(archived: boolean): Promise<void> {
        return this.wrapPromise(
            (async () => {
                let areUnread = true;

                while (areUnread) {
                    const getResponse = await this.getList({
                        size: 500,
                        read: false,
                        archived: archived,
                    });

                    areUnread = getResponse.items.length > 0;

                    if (areUnread) {
                        await this.markAsRead(getResponse.items.map((i) => i.id));
                    }
                }
            })(),
        );
    }

    async archive(notificationIds: string[]): Promise<void> {
        return this.wrapPromise(
            (async () => {
                const response = await this.fetchInternal(`${this.baseUrl}/v1/notifications/archive`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        ids: notificationIds,
                    }),
                });
                if (!response.ok) {
                    throw await createClientError(response);
                }
            })(),
        );
    }

    async unArchive(notificationIds: string[]): Promise<void> {
        return this.wrapPromise(
            (async () => {
                const response = await this.fetchInternal(`${this.baseUrl}/v1/notifications/archive`, {
                    method: 'DELETE',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        ids: notificationIds,
                    }),
                });
                if (!response.ok) {
                    throw await createClientError(response);
                }
            })(),
        );
    }

    async saveResponse(notificationId: string, responseValue: string): Promise<void> {
        return this.wrapPromise(
            (async () => {
                const response = await this.fetchInternal(`${this.baseUrl}/v1/notifications/${notificationId}/response`, {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({ response: responseValue }),
                });
                if (!response.ok) {
                    throw await createClientError(response);
                }
            })(),
        );
    }

    async createAttachmentSignature(notificationId: string, attachmentKey: string): Promise<GetAttachmentSignatureResponse> {
        return this.wrapPromise(
            (async () => {
                const response = await this.fetchInternal(
                    `${this.baseUrl}/v1/notifications/${notificationId}/attachments/${attachmentKey}/signatures`,
                    {
                        method: 'POST',
                    },
                );
                if (response.ok) {
                    return response.json();
                } else {
                    throw await createClientError(response);
                }
            })(),
        );
    }

    getDownloadAttachmentUrl(notificationId: string, attachmentKey: string, signature: string): string {
        return `${this.baseUrl}/v1/notifications/${notificationId}/attachments/${attachmentKey}?signature=${encodeURIComponent(signature)}`;
    }

    private async refreshToken(): Promise<string> {
        const token = await this.onTokenRequest();
        storeToken(this.email, token);
        return token;
    }

    private async getToken(): Promise<string> {
        const token = getToken(this.email);
        if (!token) {
            return this.refreshToken();
        }
        return token;
    }

    private async withDefaultFetchOptions(options: RequestInit): Promise<RequestInit> {
        const headers = new Headers(options.headers);
        headers.set('Authorization', 'Bearer ' + (await this.getToken()));
        options.headers = headers;
        return options;
    }

    private async fetchInternal(url: string, init?: RequestInit): Promise<Response> {
        const fetchCall = async () => {
            const options = await this.withDefaultFetchOptions(init ?? {});
            return fetch(url, options);
        };
        const response = await fetchCall();
        if (response.status === 401) {
            await this.refreshToken();
            return fetchCall();
        }
        return response;
    }

    private wrapPromise = <T>(promise: Promise<T>): Promise<T> => {
        return new Promise((resolve, reject) => {
            promise.then(
                (value) => {
                    if (!this.disposed) {
                        resolve(value);
                    }
                },
                (error) => {
                    if (!this.disposed) {
                        reject(error);
                    }
                },
            );
        });
    };
}
